import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { Subscription } from 'rxjs';
import { Permission } from '../../models/permission.model';
import { UserEdit } from '../../models/user-edit.model';
import { User } from '../../models/user.model';
import { AccountService } from "../../services/account.service";
import { AlertService, DialogType, MessageSeverity } from '../../services/alert.service';
import { fadeInOut } from '../../services/animations';
import { AuthService } from "../../services/auth.service";
import { EventService } from '../../services/event.service';
import { Utilities } from '../../services/utilities';

@Component({
    selector: 'users',
    template: require('./users.component.html'),
    styles: [require('./users.component.css')],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.None
})

export class UsersComponent implements OnInit, OnDestroy {
    private subscribers: Subscription[] = [];
    selectedUser: User[] = [];
    users: User[] = [];
    loading: boolean;
    constructor(private router: Router, private authService: AuthService, private userService: AccountService, private alertService: AlertService, private ngxLogger: NGXLogger,
        private eventService: EventService) {
    }

    ngOnInit(): void {
        this.getUser();
    }

    ngOnDestroy() {
        if (this.subscribers) {
            this.subscribers.forEach(s => s.unsubscribe());
        }
    }

    getimpersonateUse(email) {
        this.alertService.startLoadingMessage("", "Poging tot inloggen ...");
        if (this.currentUserName == "") {
            this.router.navigate(['login']);
        }
        const impersonateSubscriber = this.authService.impersonatelogin(email, this.currentUserName)
            .subscribe(
                user => {
                    // Add Extra Details in User Object to update it in Session Storage
                    this.authService.setExtraUserInfo(user, false);
                    setTimeout(() => {
                        this.alertService.stopLoadingMessage();
                    }, 500);
                    this.router.navigate(['home']);
                },
                error => {
                    this.alertService.stopLoadingMessage();
                    if (Utilities.checkNoNetwork(error)) {
                        this.alertService.showStickyMessage(Utilities.noNetworkMessageCaption, Utilities.noNetworkMessageDetail, MessageSeverity.error, error);
                    }
                    else {
                        let errorMessage = Utilities.findHttpResponseMessage("error_description", error);

                        if (errorMessage)
                            this.alertService.showStickyMessage("Niet mogelijk om in te loggen", errorMessage, MessageSeverity.error, error);
                        else
                            this.alertService.showStickyMessage("Niet mogelijk om in te loggen", "Er is een fout opgetreden tijdens het inloggen. Probeer het later opnieuw.\nError: " + error.statusText || error.status, MessageSeverity.error, error);
                    }
                });
        this.subscribers.push(impersonateSubscriber);
    }

    getUser() {
        this.loading = true;
        const usersSubscriber = this.userService.getUsers()
            .subscribe(response => {
                if (response != null) {
                    this.users = response.filter(c => !c.IsDeleted);
                    this.loading = false;
                } else {
                    this.users = [];
                }
                this.loading = false;
            }, er => {
                this.loading = false;
                this.users = [];
            });
        this.subscribers.push(usersSubscriber);
    }

    showErrorAlert(caption: string, message: string) {
        this.alertService.showMessage(caption, message, MessageSeverity.error);
    }

    delete(user: UserEdit) {
        const tempUser: any = user;
        this.alertService.showDialog(`Ben je zeker om gebruiker te verwijderen: ${tempUser.FullName} ?`, DialogType.confirm, () => {
            this.toggleProcessing(true);
            const deleUserSubscriber = this.userService.deleteUser(tempUser.Id).subscribe(response => {
                this.toggleProcessing(false);
                this.alertService.showMessage('Gebruiker', 'Met succes verwijderd!', MessageSeverity.success);
                this.getUser();
            }, err => {
                this.toggleProcessing(false);
            });
            this.subscribers.push(deleUserSubscriber);
        });
    }

    lock(user: UserEdit) {
        const tempUser: any = user;
        let msg: string = '';
        let msgsucess: string = '';

        if (user.IsLockedOut == false) {
            msg = 'Weet je zeker dat je wilt vergrendelen ';
            msgsucess = "vergrendelen";
        }

        if (user.IsLockedOut == true) {
            msg = 'Weet je zeker dat je wilt ontgrendelen ';
            msgsucess = "ontgrendelen";
        }

        this.alertService.showDialog(msg + tempUser.FullName + '?', DialogType.confirm, () => {
            this.toggleProcessing(true);
            const deleUserSubscriber = this.userService.unblockUser(tempUser.Id).subscribe(response => {
                this.toggleProcessing(false);
                this.alertService.showMessage('Gebruiker', 'Met succes ' + msgsucess + ' !', MessageSeverity.success);
                this.getUser();
            }, err => {
                this.toggleProcessing(false);
            });
            this.subscribers.push(deleUserSubscriber);
        });
    }

    userRowStyleClass(rowData: User) {
        let className: string = "";
        const expireDate = moment(rowData.ExpireDate);
        if (rowData.IsPermanent === false) {
            if (rowData.IsTrial === true && expireDate.diff(new Date(), 'days') >= 0) {
                className = "bgRowGreen";
            }
            if (rowData.IsTrial === true && expireDate.diff(new Date(), 'days') < 0 && rowData.IsExtented === false && rowData.IsEnabled === true) {
                className = "bgRowOrange";
            }
            if ((expireDate.diff(new Date(), 'days') < 0 && rowData.IsExtented === false && rowData.IsEnabled === false) || rowData.IsEnabled === false) {
                className = "bgRowRed";
            }
        }
        return className;
    }

    toggleProcessing(processState: boolean = false) {
        if (processState) {
            this.eventService.showProcessingSpinner();
        } else {
            this.eventService.hideProcessingSpinner();
        }
    }

    get currentUserName(): string {
        return this.authService.currentUser ? this.authService.currentUser.UserName : "";
    }

    get canmanageUsers() {
        return this.userService.userHasPermission(Permission.manageUsersPermission);
    }

    get canviewUsers() {
        return this.userService.userHasPermission(Permission.viewUsersPermission);
    }

    get canaddUsers() {
        return this.userService.userHasPermission(Permission.addUsersPermission);
    }

    get caneditUsers() {
        return this.userService.userHasPermission(Permission.editUsersPermission);
    }

    get candeleteUsers() {
        return this.userService.userHasPermission(Permission.deleteUsersPermission);
    }

    get canlistUsers() {
        return this.userService.userHasPermission(Permission.listUsersPermission);
    }

    get canapproveUsers() {
        return this.userService.userHasPermission(Permission.approveUsersPermission);
    }

    get canExportUsers() {
        return this.userService.userHasPermission(Permission.exportUsersPermission);
    }

    get canImpersonateUser() {
        return this.userService.userHasPermission(Permission.impersonateUserPermission);
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/users/users.component.ts